import { getBookingStatusLabel } from "../../common/bookingStatus";
import React, { useEffect, useState } from "react";
// import PropTypes from 'prop-types';
import { Box, Header, Heading, Button, DataTable, Text } from "grommet";
import { Add, Edit, Logout } from "grommet-icons";
import GuestService from "../../services/guest.service";
import {
  capitalize,
  onDatatableSearch,
  canCheckout,
  canSendKeys,
} from "@common/utils";
import { GuestModal, ImportBookingsButton } from "./";
import {
  BookingDates,
  AppInstalled,
  NoPhoneIcon,
  BookingStatus,
  NoResults,
  SmsUnreachableIcon,
} from "@shared/";
import { presentAlertPrompt } from "@shared/Alert";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentLoading, dismissLoading } from "@shared/Loading";
import "./GuestsPage.scss";
import { useFetchHotel } from "./redux/fetchHotel";
import { NoEmailIcon } from "@features/shared";
import { pmsThatCanImportBookings } from "@common/features";
import { useTranslation } from "react-i18next";

const fetchGuests = (setGuestList, setAllGuestList, serviceType, t) => {
  presentLoading();
  GuestService.getBookings("current").then(
    (response) => {
      var list = response.data.map((booking) => ({
        id: booking._id,
        bookingId: booking.bookingId,
        name:
          capitalize(booking.firstname) + " " + capitalize(booking.lastname),
        appInstalled: booking.appInstalled,
        room: booking.roomNumber ? booking.roomNumber : "-",
        dates: (
          <BookingDates
            checkinDate={booking.checkinDate}
            checkoutDate={booking.checkoutDate}
          />
        ),
        status: booking.status,
        statusName: getBookingStatusLabel(booking.status, serviceType, t),
        canCheckout: canCheckout(booking),
        canSendKeys: canSendKeys(booking),
        phone: booking.phone,
        email: booking.email,
        smsUnreachable: !!booking.smsUnreachable,
        emailUnreachable: !!booking.emailUnreachable,
        commsFailed: !!booking.commsFailed,
      }));
      dismissLoading();
      setGuestList(list);
      setAllGuestList(list);
    },
    (error) => {
      dismissLoading();
      setGuestList([]);
      setAllGuestList([]);
    },
  );
};

export default function GuestsPage() {
  const [guestList, setGuestList] = useState([]);
  const [allGuestList, setAllGuestList] = useState([]);
  const [guestModal, setGuestModal] = useState(false);
  const [guestModalProps, setGuestModalProps] = useState({
    guestId: 0,
    sendKeys: false,
  });
  const { hotel } = useFetchHotel();
  const canImportFromPms = pmsThatCanImportBookings.includes(hotel?.pmsSlug);
  const { t } = useTranslation();

  const columns = [
    {
      property: "bookingId",
      header: t("guests.colRef"),
      search: true,
    },
    {
      property: "name",
      header: t("guests.colName"),
      search: true,
      render: (datum) => (
        <Box direction="row" gap="small" align="center">
          <Text>{datum.name}</Text>
          {datum.appInstalled && (
            <AppInstalled pkey={`appinstall-${datum.id}`} />
          )}
          {!datum.phone && !datum.appInstalled && (
            <NoPhoneIcon pkey={`nophone-${datum.id}`} />
          )}
          {datum.phone && datum.smsUnreachable && (
            <SmsUnreachableIcon
              pkey={`smsunreachable-${datum.id}`}
              phone={datum.phone}
              color={
                datum.email && !datum.emailUnreachable
                  ? "status-warning"
                  : "status-critical"
              }
            />
          )}
          {!datum.email && <NoEmailIcon pkey={`noemail-${datum.id}`} />}
        </Box>
      ),
    },
    {
      property: "room",
      header: t("guests.colRoom"),
      align: "center",
      search: true,
    },
    {
      property: "dates",
      header: t("guests.colDates"),
      // render: datum =>
      //   datum.date && new Date(datum.date).toLocaleDateString('en-US'),
      align: "center",
    },
    {
      property: "statusName",
      header: t("guests.colStatus"),
      align: "center",
      search: true,
      render: (datum) => (
        <BookingStatus
          status={datum.status}
          commsFailed={datum.commsFailed}
          onClick={() => handleGuestDetails(datum.id)}
        />
      ),
    },
    {
      property: "actions",
      header: t("guests.colActions"),
      align: "start",
      render: (datum) => (
        <Box direction="row" gap="medium">
          <Button
            hoverIndicator="accent-4"
            icon={<Edit size="small" />}
            size="small"
            primary
            color="status-warning"
            onClick={() => handleGuestDetails(datum.id)}
          />
          {/* {!!datum.canSendKeys && (
            <Button
              hoverIndicator="accent-1"
              icon={<License size="small" />}
              size="small"
              primary
              color="status-ok"
              onClick={() => handleGuestModalKeys(datum.id)}
            />
          )} */}
          {!!datum.canCheckout && (
            <div>
              <Button
                hoverIndicator="dark-1"
                icon={<Logout size="small" />}
                size="small"
                primary
                color="status-critical"
                onClick={() => handleCheckout(datum.id)}
              />
              {datum.canCheckout}
            </div>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchGuests(setGuestList, setAllGuestList, hotel?.serviceType, t);
  }, [hotel?.serviceType, t]);

  const handleAddGuest = () => {
    setGuestModalProps({ guestId: 0, sendKeys: false });
    setGuestModal(true);
  };

  // const handleGuestModalKeys = (guestId) => {
  //   setGuestModalProps({ guestId, sendKeys: true });
  //   setGuestModal(true);
  // };

  const handleGuestModalClose = (refresh) => {
    setGuestModal(false);
    if (refresh) fetchGuests(setGuestList, setAllGuestList, hotel?.serviceType, t);
  };

  const handleGuestDetails = (guestId) => {
    window.location.href = "guests/" + guestId;
    //setTimeout(()=>{setGuestModalProps({guestId})},2000);
  };

  const handleCheckout = (guestId) => {
    function checkoutGuest() {
      GuestService.checkout(guestId).then(
        (response) => {
          presentToastSuccess("Guest successfully checked out.");
          fetchGuests(setGuestList, setAllGuestList, hotel?.serviceType, t);
        },
        (error) => {
          presentToastErrorContent(error);
          fetchGuests(setGuestList, setAllGuestList, hotel?.serviceType, t);
        },
      );
    }

    presentAlertPrompt({
      title: t("guests.deleteConfirmTitle"),
      message: t("guests.deleteConfirmMessage"),
      onOK: checkoutGuest,
      buttonOKText: t("common.ok"),
      buttonOKColor: "status-critical",
    });
  };

  return (
    <Box align="center" justify="start" fill pad="medium">
      {guestModal && (
        <GuestModal
          guestId={guestModalProps.guestId}
          onClose={handleGuestModalClose}
          sendKeys={guestModalProps.sendKeys}
        />
      )}
      <Header
        align="center"
        direction="row"
        justify="between"
        gap="medium"
        fill="horizontal"
      >
        <Heading level="2">
          {allGuestList && allGuestList.length > 0
            ? t("guests.titleWithCount", { count: allGuestList.length })
            : t("guests.title")}
        </Heading>
        <Box direction="row" gap="small">
          {canImportFromPms && (
            <ImportBookingsButton
              onComplete={() => fetchGuests(setGuestList, setAllGuestList, hotel?.serviceType, t)}
            />
          )}
          <Button
            icon={<Add size="small" />}
            label={t("guests.addGuest")}
            primary
            onClick={handleAddGuest}
          />
        </Box>
      </Header>
      <Box fill="horizontal">
        {allGuestList.length ? (
          <Box>
            <DataTable
              columns={columns}
              data={guestList}
              primaryKey="id"
              step={guestList.length || 1}
              onClickRow={(event) => {
                if (
                  event.target.localName !== "button" &&
                  event.target.localName !== "svg" &&
                  event.target.localName !== "path"
                )
                  handleGuestDetails(event.datum.id);
              }}
              onSearch={(search) =>
                onDatatableSearch(search, allGuestList, setGuestList)
              }
            />
          </Box>
        ) : (
          <NoResults>
            This list is empty. <br />
            Start adding or importing reservations.
          </NoResults>
        )}
      </Box>
    </Box>
  );
}

GuestsPage.propTypes = {};
GuestsPage.defaultProps = {};
